import { Divider, Paper, Stack, Typography, type TypographyOwnProps } from "@mui/material";
import type { PortalWidget, PredefinedPortalWidgetType } from "@entrilia/shared/api/portalWidgetsTypes";
import type { ReportInfo } from "../../../../api/types/svTypes";
import DatabaseIcon from "@entrilia/shared/icons/DatabaseIcon";
import PortalWidgetAvatar from "./PortalWidgetAvatar";
import PortalWidgetStatusTag from "./PortalWidgetStatusTag";
import { getWidgetDataSourceType } from "./portalWidgetsHelper";

interface Props {
  widget: PortalWidget;
  selected: boolean;
  onSelect: (widgetId: string) => void;
  systemReports: ReportInfo[];
  hasReportsViewPermissions: boolean;
}

const widgetSubtitleMap: Record<PredefinedPortalWidgetType, string> = {
  Balance: "Investor balances as of effective reporting date.",
  CashFlows: "Capital Calls and distributions with their payment status.",
  Portfolio: "Schedule of investments across the portfolio.",
  News: "Latest articles and updates for investors.",
};

const getWidgetSubtitle = (predefinedType?: PredefinedPortalWidgetType) => {
  if (predefinedType) {
    return widgetSubtitleMap[predefinedType];
  }
  return "User-defined";
};

const getDataSourceLabel = (
  widget: PortalWidget,
  systemReports: ReportInfo[],
  hasReportsViewPermissions: boolean
): { dataSourceLabel: string; dataSourceColor: TypographyOwnProps["color"] } => {
  const dataSourceType = getWidgetDataSourceType(widget);
  if (dataSourceType === "CashFlowTransactions") {
    return { dataSourceLabel: "Cashflow Transactions", dataSourceColor: "textPrimary" };
  }

  if (dataSourceType === "InvestorCommunications") {
    return { dataSourceLabel: "Investor Communications", dataSourceColor: "textPrimary" };
  }

  if (!hasReportsViewPermissions) {
    return {
      dataSourceLabel: "System Report (insufficient permissions to view details)",
      dataSourceColor: "warning",
    };
  }

  const report = systemReports.find((r) => r.reportId === widget.reportId);
  if (report === undefined) {
    return {
      dataSourceLabel: "System Report: not configured",
      dataSourceColor: "warning",
    };
  }

  return {
    dataSourceLabel: report.name,
    dataSourceColor: "textPrimary",
  };
};

const PortalWidgetCard = ({ widget, selected, onSelect, systemReports, hasReportsViewPermissions }: Props) => {
  const { dataSourceLabel, dataSourceColor } = getDataSourceLabel(widget, systemReports, hasReportsViewPermissions);

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(widget.id)}
      sx={{
        cursor: "pointer",
        px: 2,
        py: 1.5,
        borderColor: selected ? "primary.main" : undefined,
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <PortalWidgetAvatar predefinedWidgetType={widget.predefinedType} />
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">{widget.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {getWidgetSubtitle(widget.predefinedType)}
            </Typography>
          </Stack>
        </Stack>
        <PortalWidgetStatusTag status={widget.status} />
      </Stack>
      <Divider sx={{ borderStyle: "dashed", my: 1.5 }} />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <DatabaseIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="caption" color="textSecondary">
          Data Source:
        </Typography>
        <Typography variant="subtitle2" color={dataSourceColor}>
          {dataSourceLabel}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default PortalWidgetCard;
